import { useCallback, useContext, useEffect, useState } from "react";
import { ContainedButton, NFTContainer } from "./styles";
import { Typography, Tooltip, Box, IconButton } from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import { TalkJsContext, TalkJsContextInterface } from "../../contexts";
import { FractionalTransaction } from "../../proto/api_pb";
import { QueryTransactionsRequest } from "../../proto/api_pb";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { OmmuseClient } from "../../proto/api_grpc_web_pb";
import { mapToTalkUser } from "../../utils/TalkjsUtils";
import Mixpanel from "../../utils/mixpanel";
import { modalStyles as styles } from "./styles";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import { FacebookShareButton, TwitterShareButton } from "react-share";
import { EmailShareButton } from "react-share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { shortenLink } from "../../services/utilService";
import { toast } from "react-toastify";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Track from "../../components/ExploreBase/Track";
import { NftTrack } from "../../components/ExploreBase/types";
import { FONT_FAMILY } from "../../theme";

const openseaURL = process.env.REACT_APP_OPENSEA_URL ?? "";
const erc721ContractAddress = process.env.REACT_APP_ECR721 ?? "";

const NFTPage: React.FC = () => {
  const { txnID } = useParams();
  const { handleChatClick, setParticipants, createConversation } =
    useContext<TalkJsContextInterface>(TalkJsContext);
  const [createGroupChatClicked, setCreateGroupChatClicked] = useState(false);
  const [nftInfo, setNftInfo] = useState<FractionalTransaction>();
  const [track, setTrack] = useState<NftTrack>();
  const [currentUserId, setCurrentUserId] = useState("");
  const [bitlyUrl, setBitlyUrl] = useState<string>("");

  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, getIdTokenClaims } = useAuth0();
  useEffect(() => {
    document.title = "OmMuse NFT";
  }, []);

  useEffect(() => {
    const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
    const metadata = {
      "Access-Control-Allow-Origin": "*",
    };
    const request = new QueryTransactionsRequest();

    request.setOmmuseTxnIdsList([txnID ?? ""]);
    ommuseClient.queryTransactions(request, metadata, function (err, response) {
      if (err) {
        console.log("Get NFT info Error");
      } else {
        const list = response.getFractionalTransactionList();
        if (list.length === 0) {
          console.log("No NFT is found - Getting NFT info on App");
          navigate("/not-found");
        } else {
          setNftInfo(list[0]);
          setTrack({
            id: parseInt(list[0].getTokenId()),
            audio: list[0].getSongUrl(),
            waveSurfer: undefined,
            isPlaying: false,
            image: list[0].getImageUrl(),
            title: list[0].getTitle(),
            subtitle: list[0].getArtist(),
            genre: list[0].getGenresList()[0],
          });
        }
      }
    });
  }, [navigate, txnID]);

  // shortening the URLs using bitly api
  useEffect(() => {
    const urlToBitly = async () => {
      try {
        if (window.location.hostname === "localhost") {
          const response = await shortenLink(
            `https://app-dev.ommuse.com/nft/${nftInfo?.getOmmuseTxnId()}`
          );
          setBitlyUrl(response.data.link);
        } else {
          const response = await shortenLink(`${window.location.href}`);
          setBitlyUrl(response.data.link);
        }
      } catch (error) {
        console.log("Error for bitly URL in CardModal" + error);
      }
    };
    urlToBitly();
  }, [nftInfo]);

  const handleCopy = async () => {
    navigator.clipboard.writeText(bitlyUrl);
    toast.success("Link copied!");
  };

  const setAudioTrack = useCallback(
    (id: number, audioTrack: WaveSurfer) => {
      if (track && !track.waveSurfer) {
        track.waveSurfer = audioTrack;
      }
    },
    [track]
  );

  const handlePlay = (id: number) => {
    const song = track?.waveSurfer;
    if (song) {
      song.playPause();
      Mixpanel.track("play_track_nft_page", { id: track!.id });
      setTrack({
        ...track,
        isPlaying: track.waveSurfer?.isPlaying() ?? false,
      });
    }
  };

  const handleStop = (id: number) => {
    const song = track?.waveSurfer;
    if (song) {
      song.stop();
      Mixpanel.track("stop_track_nft_page", { id: track!.id });
      setTrack({
        ...track,
        isPlaying: track.waveSurfer?.isPlaying() ?? false,
      });
    }
  };

  const handleCreateGroupChat = () => {
    try {
      if (
        handleChatClick &&
        setParticipants &&
        nftInfo?.getFractionalTransactionPartList()
      ) {
        handleChatClick();
        const participants = nftInfo
          ?.getFractionalTransactionPartList()
          .filter((c) => c.getUsername())
          .map(mapToTalkUser);
        setParticipants(participants);
        Mixpanel.track("create_chat_grop_nft_page");
        setCreateGroupChatClicked(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (createGroupChatClicked && createConversation) {
      createConversation();
    }
  }, [createConversation, createGroupChatClicked]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const idToken = await getIdTokenClaims();
        if (idToken) {
          setCurrentUserId(idToken.sub);
        }
      }
    })();
  }, [getIdTokenClaims, isAuthenticated]);

  const otherContributors = nftInfo
    ?.getFractionalTransactionPartList()
    .filter((c) => c.getUserId() !== currentUserId);

  return (
    <div className="container" style={styles.container}>
      <NFTContainer>
        {track && (
          <Track
            track={track}
            setAudioTrack={setAudioTrack}
            handlePlay={handlePlay}
            handleStop={handleStop}
          />
        )}
        <div style={styles.information}>
          <div style={{ flexGrow: 1 }}>
            <Typography sx={styles.descriptionHeader}>Description</Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY }}>
              {nftInfo?.getDescription()}
            </Typography>
            {isAuthenticated ? (
              otherContributors?.length && otherContributors?.length > 1 ? (
                <ContainedButton
                  size="medium"
                  variant="contained"
                  onClick={handleCreateGroupChat}
                >
                  Send Message
                </ContainedButton>
              ) : (
                <div></div>
              )
            ) : (
              <ContainedButton
                size="medium"
                variant="contained"
                onClick={() =>
                  loginWithRedirect({
                    appState: { targetUrl: "/" },
                  })
                }
              >
                Log in | Sign up
              </ContainedButton>
            )}
          </div>
          <Box sx={styles.contributeShareContainer}>
            <Typography sx={styles.contributorsHeading}>
              Contributors
            </Typography>
            <div style={styles.contributorsPhotoContainer}>
              {nftInfo
                ?.getFractionalTransactionPartList()
                .filter((c) => c.getUsername())
                .map((contributor, index) => {
                  return (
                    <NavLink
                      key={contributor.getUserId()}
                      to={`/user-profile/${contributor.getUsername()}`}
                      style={styles.contributorNavLink}
                    >
                      <Tooltip
                        title={contributor.getUsername()}
                        placement="top"
                      >
                        <img
                          style={styles.contributorsPhotos}
                          src={contributor.getProfilePicture()}
                          alt=""
                        />
                      </Tooltip>
                    </NavLink>
                  );
                })}
            </div>
            <div style={styles.openSeaUrlContainer}>
              <Tooltip title={`Open Sea Listing`} placement="top">
                <NavLink
                  to={`${openseaURL}/${erc721ContractAddress}/${nftInfo?.getTokenId()}`}
                >
                  <img
                    src="/assets/images/opensea.svg"
                    alt="opensea"
                    style={{ width: 22, marginRight: 5 }}
                  />
                </NavLink>
              </Tooltip>
              <Tooltip title="Copy" placement="top">
                <IconButton
                  disableFocusRipple
                  style={{ marginRight: 5 }}
                  onClick={handleCopy}
                  size="small"
                >
                  <ContentCopyIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Facebook" placement="top">
                <FacebookShareButton url={bitlyUrl} hashtag={"#ommuse"}>
                  <FacebookIcon color="primary" style={{ marginRight: 10 }} />
                </FacebookShareButton>
              </Tooltip>
              <Tooltip title="Twitter" placement="top">
                <div>
                  <TwitterShareButton
                    url={bitlyUrl}
                    title="Check out this NFT"
                    hashtags={["OmMuse"]}
                    via="OmMuseMusic"
                  >
                    <TwitterIcon color="primary" style={{ marginRight: 10 }} />
                  </TwitterShareButton>
                </div>
              </Tooltip>
              <Tooltip title="Email" placement="top">
                <EmailShareButton
                  url={bitlyUrl}
                  subject="Music NFT on OmMuse"
                  body={`Hello, check out this music NFT:`}
                  openShareDialogOnClick={true}
                  onClick={() => {}}
                >
                  <MailOutlineIcon
                    color="primary"
                    style={{ marginRight: 10 }}
                  />
                </EmailShareButton>
              </Tooltip>
            </div>
          </Box>
        </div>
      </NFTContainer>
    </div>
  );
};

export default NFTPage;
